import React, { useState, createContext, useContext, useRef, useEffect } from "react";
import "./App.css";
import Header from "./components/Header/Header";
import OptimizedMap from "./components/Map/OptimizedMap";
import ReservationModal from "./components/Map/ReservationModal";
import GoogleAuthHandler from "./components/Auth/GoogleAuthHandler";
import LoginModal from "./components/Auth/LoginModal";

import FavoritesList from "./components/Favorites/FavoritesList";
import SearchResults from "./components/SearchResults/SearchResults";
import { unifiedSearch, getProjectFloors, getParcelWithProject, toggleFavorite } from "./services/api";
import {
  getLanguagePreference,
  setLanguagePreference,
  getSidebarPositionPreference,
  setSidebarPositionPreference,
  getActualSidebarPosition
} from "./utils/userPreferences";
import { ProjectsProvider, useProjects } from "./contexts/ProjectsContext";
import { useSettings } from "./hooks/useSettings";
import { GlobalModalsProvider } from "./components/Common/GlobalModals";
import { fitMapToBounds } from "./utils/mapBoundsCalculator";

import ErrorBoundary from "./components/ErrorBoundary/ErrorBoundary";
import PreviewAccessGate from "./components/Auth/PreviewAccessGate";

// Language Context
const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return context;
};

// Inner App component that uses ProjectsContext
function AppContent() {
  // Use projects context
  const { projects, selectedFloor, setSelectedFloor } = useProjects();

  // Use settings context
  const { settings } = useSettings();

  // Initialize selectedProject as null (don't auto-load from localStorage)
  // This prevents automatic navigation to the last viewed project on page load
  const [selectedProject, setSelectedProject] = useState(() => {
    return null;
  });
  const [sharedProjectId, setSharedProjectId] = useState(null);
  const [isSharedView, setIsSharedView] = useState(false);

  // Shared unit state
  const [sharedUnitId, setSharedUnitId] = useState(null);
  const [isSharedUnitView, setIsSharedUnitView] = useState(false);

  // Save selectedProject to localStorage when it changes
  useEffect(() => {
    if (selectedProject !== null) {
      try {
        localStorage.setItem('realmap_selectedProject', selectedProject.toString());
      } catch (error) {
        console.warn('Error saving selectedProject to localStorage:', error);
      }
    } else {
      try {
        localStorage.removeItem('realmap_selectedProject');
      } catch (error) {
        console.warn('Error removing selectedProject from localStorage:', error);
      }
    }
  }, [selectedProject]);
  // Load language from localStorage or default to Arabic
  const [language, setLanguage] = useState(() => {
    return getLanguagePreference();
  });
  // Load sidebar position preference from localStorage
  const [sidebarPosition, setSidebarPosition] = useState(() => {
    return getSidebarPositionPreference();
  });
  const [mapStyle, setMapStyle] = useState("satellite-v9"); // Default map style - custom satellite style
  const [filtersVisible, setFiltersVisible] = useState(false);
  const [statusFilters, setStatusFilters] = useState([]); // فلاتر الحالة الجديدة
  const [priceFilter, setPriceFilter] = useState({ min: 0, max: 1000000 }); // فلتر السعر
  const [usageFilters, setUsageFilters] = useState([]); // فلاتر الاستخدام
  const [isPriceFilterActive, setIsPriceFilterActive] = useState(false); // حالة فلتر السعر
  const [defaultPriceRange, setDefaultPriceRange] = useState({ min: 0, max: 1000000 }); // النطاق الافتراضي للأسعار
  const [priceStats, setPriceStats] = useState({ min: 0, max: 1000000 }); // إحصائيات الأسعار الفعلية
  const [currentZoom, setCurrentZoom] = useState(6); // مستوى التكبير الحالي
  const parcelSearchRef = useRef(null);
  const searchPanelRef = useRef();
  const pendingUnitSearchScheduledRef = useRef(null);

  const mapRef = useRef(null);

  // Reservation modal state
  const [reservationModalOpen, setReservationModalOpen] = useState(false);
  const [selectedParcelForReservation, setSelectedParcelForReservation] = useState(null);
  const [selectedProjectForReservation, setSelectedProjectForReservation] = useState(null);

  // Login modal state for reservation
  const [loginModalForReservationOpen, setLoginModalForReservationOpen] = useState(false);

  // Favorites modal state
  const [favoritesVisible, setFavoritesVisible] = useState(false);

  // Favorites mode state for SearchResults
  const [isFavoritesMode, setIsFavoritesMode] = useState(false);

  // Project search state
  const [projectSearchQuery, setProjectSearchQuery] = useState('');
  const [projectFilters, setProjectFilters] = useState({ status: [], city: '' });
  const [recentProjects, setRecentProjects] = useState([]);

  // Project floors state
  const [projectFloors, setProjectFloors] = useState([]);



  // Header transition state
  const [headerTransitioning, setHeaderTransitioning] = useState(false);
  const [previousZoom, setPreviousZoom] = useState(6);

  // Map interaction state for recent projects
  const [mapInteracted, setMapInteracted] = useState(false);

  // Search results state
  const [searchResults, setSearchResults] = useState(null);
  const [searchResultsVisible, setSearchResultsVisible] = useState(false);
  const [isSearchLoading, setIsSearchLoading] = useState(false);
  const [hideUnitsTabInResults, setHideUnitsTabInResults] = useState(false);
  const [searchPagination, setSearchPagination] = useState({
    projects: { page: 1, perPage: 10 },
    units: { page: 1, perPage: 10 },
    developers: { page: 1, perPage: 10 }
  });
  const [currentSearchQuery, setCurrentSearchQuery] = useState('');
  const [currentSearchType, setCurrentSearchType] = useState('all');
  const [searchFilters, setSearchFilters] = useState({
    projects: {},
    units: {},
    developers: {}
  });
  const [searchSort, setSearchSort] = useState({
    projects: { sortBy: 'created_at', sortOrder: 'desc' },
    units: { sortBy: 'created_at', sortOrder: 'desc' },
    developers: { sortBy: 'created_at', sortOrder: 'desc' }
  });
  const [pendingUnitSelect, setPendingUnitSelect] = useState(null);

  // Tracks which parcel ids currently have an in-flight favorite toggle so the
  // UnitCard can render a spinner instead of the heart icon.
  const [favoriteLoadingIds, setFavoriteLoadingIds] = useState({});

  // Search Results History
  const [searchResultsHistory, setSearchResultsHistory] = useState([]);
  const [customSearchTitle, setCustomSearchTitle] = useState(null);
  
  // Background loading state for single unit details
  const [isUnitBackgroundLoading, setIsUnitBackgroundLoading] = useState(false);

  // Tracks whether the shared-unit (?unit_id=...) deep link has been resolved
  // so we don't re-fetch / re-select on every render.
  const sharedUnitResolvedRef = useRef(false);

  // Check for shared project or unit in URL on component mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const projectId = urlParams.get('project');
    const shared = urlParams.get('shared');
    const unitId = urlParams.get('unit_id');

    if (projectId && shared === 'true') {
      const numericProjectId = parseInt(projectId);
      setSharedProjectId(numericProjectId);
      setSelectedProject(numericProjectId);
      setIsSharedView(true);

      // Check if this is a unit sharing link
      if (unitId) {
        const numericUnitId = parseInt(unitId);
        setSharedUnitId(numericUnitId);
        setIsSharedUnitView(true);
      } else {
      }
    }
  }, []);

  // Save language preference to localStorage whenever language changes
  useEffect(() => {
    setLanguagePreference(language);
  }, [language]);

  // Save sidebar position preference to localStorage whenever it changes
  useEffect(() => {
    setSidebarPositionPreference(sidebarPosition);
  }, [sidebarPosition]);

  // Load floors when selected project changes and it's a residential project
  useEffect(() => {
    const loadProjectFloors = async () => {
      if (selectedProject) {
        // Check if project is residential
        const project = projects.find(p => p.id === selectedProject);

        if (!project || project.project_type !== 'residential') {
          setProjectFloors([]);
          setSelectedFloor(null);
          return;
        }

        // Reset selectedFloor before loading new floors
        setSelectedFloor(null);

        try {
          const response = await getProjectFloors(selectedProject);
          const floors = response.data.data || [];
          setProjectFloors(floors);
        } catch (error) {
          console.error('❌ Error loading project floors:', error);
          console.error('❌ Error details:', error.response?.data || error.message);
          setProjectFloors([]);
        }
      } else {
        setProjectFloors([]);
      }
    };

    loadProjectFloors();
  }, [selectedProject, projects]);

  // Auto-select default floor when projectFloors loads (floor_number=1 or first available)
  useEffect(() => {
    if (projectFloors && projectFloors.length > 0 && !selectedFloor) {
      const defaultFloor = projectFloors.find((f) => f.floor_number === 1) || projectFloors[0];
      if (defaultFloor) {
        setSelectedFloor(defaultFloor);
      }
    }
  }, [projectFloors]);

  // Handle pending unit selection
  useEffect(() => {
    if (pendingUnitSelect) {
      const unit = pendingUnitSelect;
      const projectId = unit.project?.id || unit.block?.project?.id;

      // Wait until the selectedProject state is updated
      if (projectId === selectedProject) {
        
        // If the unit has a floor (residential), wait for floors to load and update selectedFloor
        if (unit.floor) {
          const project = projects.find(p => p.id === selectedProject);
          if (project?.project_type === 'residential') {
            // Need project floors to be loaded first
            if (projectFloors && projectFloors.length > 0) {
              if (!selectedFloor || selectedFloor.id !== unit.floor.id) {
                // Set the floor and wait for next render
                setSelectedFloor(unit.floor);
                return;
              }
            } else {
              // Still waiting for projectFloors to load
              return;
            }
          }
        }
        
        // If we reach here, project and floor states are aligned.
        // Wait a small delay to ensure the map component processed these state changes
        // and fetched corresponding layers before we trigger the parcel search.
        // Keep pendingUnitSelect set until the search call returns so the
        // external-project-selection effect can detect we're navigating to a unit
        // (it inspects pendingUnitSelectRef inside its moveend handler).
        // Guard against re-triggering when unrelated dependencies change while
        // the search is in flight.
        if (parcelSearchRef.current && pendingUnitSearchScheduledRef.current !== unit.id) {
          pendingUnitSearchScheduledRef.current = unit.id;
          const targetUnit = unit;
          setTimeout(async () => {
            try {
              if (parcelSearchRef.current) {
                await parcelSearchRef.current(targetUnit);
              }
            } finally {
              pendingUnitSearchScheduledRef.current = null;
              setPendingUnitSelect(null);
            }
          }, 500);
        }
      }
    }
  }, [pendingUnitSelect, selectedProject, selectedFloor, projectFloors, projects, setSelectedFloor]);

  const toggleLanguage = () => {
    const newLanguage = language === "ar" ? "en" : "ar";
    setLanguage(newLanguage);
    // Language preference will be automatically saved to localStorage via useEffect
    // تفضيل اللغة سيتم حفظه تلقائياً في localStorage عبر useEffect
  };

  const toggleSidebarPosition = () => {
    // Cycle through: auto -> left -> right -> auto
    let newPosition;
    if (sidebarPosition === 'auto') {
      newPosition = 'left';
    } else if (sidebarPosition === 'left') {
      newPosition = 'right';
    } else {
      newPosition = 'auto';
    }
    setSidebarPosition(newPosition);
  };

  // Get the actual sidebar position based on preference and language
  const actualSidebarPosition = getActualSidebarPosition(language, sidebarPosition);
  const isRTLLayout = actualSidebarPosition === 'right';

  const changeMapStyle = (style) => {
    setMapStyle(style);
  };

  const handleParcelSearch = (queryData) => {
    if (parcelSearchRef.current && typeof parcelSearchRef.current === 'function') {
      parcelSearchRef.current(queryData);
    } else {
      console.warn('Parcel search function not ready yet');
    }
  };

  // Handle parcel selection from map directly (replacing ParcelDetailsModal)
  const handleParcelSelectMap = (parcelData, mapProjectData = null) => {
    // 1. Initial temporary show with known data
    let title = language === 'ar' ? `وحدة رقم ${parcelData.parcel_number || ''}` : `Unit number ${parcelData.parcel_number || ''}`;

    // Manage history pushing first before clearing/showing search results loader
    if (searchResultsVisible) {
      const isSingleView = Boolean(customSearchTitle);
      const isSameSingleUnit =
        isSingleView &&
        searchResults?.units?.length === 1 &&
        searchResults.units[0].id === parcelData.id;

      if (isSameSingleUnit) {
        // Already viewing this unit's single-view card — just expand the panel.
        if (searchPanelRef.current && typeof searchPanelRef.current.expand === 'function') {
          searchPanelRef.current.expand();
        }
      } else if (!isSingleView) {
        // Coming from a results list (or other multi-item view) — push it onto history.
        setSearchResultsHistory(prev => [
          ...prev,
          {
            results: searchResults,
            query: currentSearchQuery,
            type: currentSearchType,
            title: customSearchTitle,
            isFavoritesMode: isFavoritesMode
          }
        ]);
      }
      // else: already in a different single-view (unit/project) — replace it without
      // stacking single-views on top of each other in history.
    } else {
      setSearchResultsHistory([]);
      setSearchResultsVisible(true);
    }

    // Exit favorites mode while viewing the unit details so the single-unit view shows
    if (isFavoritesMode) {
      setIsFavoritesMode(false);
    }
    
    // Format fallback / local data
    let fallbackProject = parcelData.project || (parcelData.block && parcelData.block.project);
    if (!fallbackProject) {
      if (mapProjectData) {
        fallbackProject = mapProjectData;
      } else if (selectedProject && projects?.length > 0) {
        fallbackProject = projects.find(p => p.id === selectedProject);
      }
    }
    fallbackProject = fallbackProject || {};
    let fallbackDeveloper = fallbackProject.developer || {};

    // Construct developer from project if missing
    if (!fallbackDeveloper.id && !fallbackDeveloper.name) {
      if (fallbackProject.developer_name_ar || fallbackProject.developer_name_en) {
        fallbackDeveloper = {
          id: fallbackProject.developer_id || 'unknown',
          name: language === 'ar' ? (fallbackProject.developer_name_ar || fallbackProject.developer_name_en) : (fallbackProject.developer_name_en || fallbackProject.developer_name_ar),
          logo_url: fallbackProject.developer_logo_url,
          projects_count: 1, // At least the current project
          city: fallbackProject.city
        };
      }
    }

    const fallbackParcelData = {
      ...parcelData,
      project: fallbackProject
    };

    const fallbackResults = {
      units: [fallbackParcelData],
      projects: fallbackProject.id ? [fallbackProject] : [],
      developers: fallbackDeveloper.id || fallbackDeveloper.name ? [fallbackDeveloper] : [],
      summary: {
        total_units: 1,
        total_projects: fallbackProject.id ? 1 : 0,
        total_developers: fallbackDeveloper.id || fallbackDeveloper.name ? 1 : 0
      },
      pagination: { total: 1, current_page: 1, per_page: 10 }
    };

    // Show initial data instantly
    setIsSearchLoading(false);
    setSearchResults(fallbackResults);
    setCustomSearchTitle(title);
    setCurrentSearchType('all');
    setHideUnitsTabInResults(false);
    if (searchPanelRef.current) {
      if (typeof searchPanelRef.current.expand === 'function') {
        searchPanelRef.current.expand();
      }
      if (typeof searchPanelRef.current.setActiveTab === 'function') {
        searchPanelRef.current.setActiveTab('units');
      }
    }

    // 2. Fetch full unit details from the API in the background
    setIsUnitBackgroundLoading(true);
    getParcelWithProject(parcelData.id)
      .then(response => {
        const fullParcelData = response?.data?.data;
        if (fullParcelData) {
          let project = fullParcelData.project || (fullParcelData.block && fullParcelData.block.project);
          if (!project) {
            if (mapProjectData) project = mapProjectData;
            else if (selectedProject && projects?.length > 0) project = projects.find(p => p.id === selectedProject);
          }
          project = project || {};
          let developer = project.developer || {};

          // Construct developer from project if missing
          if (!developer.id && !developer.name) {
            if (project.developer_name_ar || project.developer_name_en) {
              developer = {
                id: project.developer_id || 'unknown',
                name: language === 'ar' ? (project.developer_name_ar || project.developer_name_en) : (project.developer_name_en || project.developer_name_ar),
                logo_url: project.developer_logo_url,
                projects_count: 1,
                city: project.city
              };
            }
          }

          const updatedParcelData = {
            ...fullParcelData,
            project: project
          };

          const formattedResults = {
            units: [updatedParcelData],
            projects: project.id ? [project] : [],
            developers: developer.id || developer.name ? [developer] : [],
            summary: {
              total_units: 1,
              total_projects: project.id ? 1 : 0,
              total_developers: developer.id || developer.name ? 1 : 0
            },
            pagination: { total: 1, current_page: 1, per_page: 10 }
          };
          
          const newTitle = language === 'ar' ? `وحدة رقم ${updatedParcelData.parcel_number || ''}` : `Unit number ${updatedParcelData.parcel_number || ''}`;
          
          // Only update if the user hasn't browsed away
          setSearchResults(current => {
             if (current?.units?.length === 1 && current.units[0].id === parcelData.id) {
                 setCustomSearchTitle(newTitle);
                 return formattedResults;
             }
             return current;
          });
        }
      })
      .catch(error => {
        console.error("Error fetching parcel details in background:", error);
      })
      .finally(() => {
        // Ensure another request hasn't started
        setSearchResults(current => {
           if (current?.units?.length === 1 && current.units[0].id === parcelData.id) {
               setIsUnitBackgroundLoading(false);
           }
           return current;
        });
      });
  };

  const handleBackToPreviousResults = () => {
    if (searchResultsHistory.length > 0) {
      const prev = searchResultsHistory[searchResultsHistory.length - 1];
      setSearchResultsHistory(history => history.slice(0, -1));

      setSearchResults(prev.results);
      setCurrentSearchQuery(prev.query);
      setCurrentSearchType(prev.type);
      setCustomSearchTitle(prev.title);
      setIsFavoritesMode(Boolean(prev.isFavoritesMode));
      setIsUnitBackgroundLoading(false);
      setIsSearchLoading(false);
      setHideUnitsTabInResults(false);

      if (searchPanelRef.current && typeof searchPanelRef.current.expand === 'function') {
        searchPanelRef.current.expand();
      }
    }
  };

  const handleZoomChange = (zoom) => {
    // Check if we're crossing the threshold between project and unit modes
    // const isModeSwitching = (previousZoom < 12 && zoom >= 12) || (previousZoom >= 12 && zoom < 12);

    // if (isModeSwitching) {
    //   setHeaderTransitioning(true);
    //   // Reset transition state after animation completes
    //   setTimeout(() => {
    //     setHeaderTransitioning(false);
    //   }, 400);
    // }

    setPreviousZoom(currentZoom);
    setCurrentZoom(zoom);
  };

  // Handle map interaction (pan, zoom, drag)
  const handleMapInteraction = () => {
    setMapInteracted(true);
  };

  const toggleFilters = () => {
    setFiltersVisible(!filtersVisible);
  };

  // دمج جميع الفلاتر في كائن واحد للخريطة
  const combineFilters = () => {
    return {
      salesStatus: statusFilters,
      priceRange: priceFilter,
      services: usageFilters,
      isPriceFilterActive: isPriceFilterActive
    };
  };

  const handleStatusFiltersChange = (newFilters) => {
    setStatusFilters(newFilters);
  };

  const handlePriceFilterChange = (newFilter) => {
    // إزالة المعلومات الإضافية قبل حفظ الفلتر
    const { isExplicitlyActivated, isExplicitlyDeactivated, ...cleanFilter } = newFilter;
    setPriceFilter(cleanFilter);

    // تحديد ما إذا كان فلتر السعر نشطًا
    let isActive = false;
    if (isExplicitlyDeactivated) {
      // إذا تم إلغاؤه صراحة من خلال زر "مسح"
      isActive = false;
    } else if (isExplicitlyActivated) {
      // إذا تم تفعيله صراحة من خلال زر "تطبيق"
      isActive = true;
    } else {
      // تحديد بناءً على القيم (مختلف عن القيم الافتراضية)
      isActive = cleanFilter.min !== defaultPriceRange.min || cleanFilter.max !== defaultPriceRange.max;
    }

    setIsPriceFilterActive(isActive);

  };

  const handleUsageFiltersChange = (newFilters) => {
    setUsageFilters(newFilters);
  };

  // Function to clear all selected data when home button is clicked
  const handleClearAllData = () => {
    // Clear project selection
    setSelectedProject(null);
    setSelectedFloor(null);

    // Clear floor data
    setProjectFloors([]);

    // Clear search results
    setSearchResults(null);
    setSearchResultsVisible(false);
    setSearchResultsHistory([]);
    setCustomSearchTitle(null);

    // Clear filters
    setStatusFilters([]);
    setUsageFilters([]);
    setPriceFilter({ min: 0, max: 1000000 });
    setIsPriceFilterActive(false);

    // Clear project filters
    setProjectFilters({ status: [], city: '' });

    // Clear recent projects
    setRecentProjects([]);
  };

  // Handle price statistics update from map
  const handlePriceStatsUpdate = (stats) => {
    setPriceStats(stats);
    setDefaultPriceRange(stats);
  };

  // Handle reservation
  const handleReservation = (parcelData, projectData) => {

    // Check if user is logged in
    const user = localStorage.getItem('user');
    const token = localStorage.getItem('token');

    if (!user || !token) {
      // User is not logged in, show login modal first
      setSelectedParcelForReservation(parcelData);
      setSelectedProjectForReservation(projectData);
      setLoginModalForReservationOpen(true);
    } else {
      // User is logged in, proceed with reservation
      setSelectedParcelForReservation(parcelData);
      setSelectedProjectForReservation(projectData);
      setReservationModalOpen(true);
    }
  };

  const handleReservationClose = () => {
    setReservationModalOpen(false);
    setSelectedParcelForReservation(null);
    setSelectedProjectForReservation(null);
  };

  // Handle login success for reservation
  const handleLoginSuccessForReservation = () => {
    setLoginModalForReservationOpen(false);
    setReservationModalOpen(true);
  };

  // Handle login modal close for reservation
  const handleLoginModalCloseForReservation = () => {
    setLoginModalForReservationOpen(false);
    setSelectedParcelForReservation(null);
    setSelectedProjectForReservation(null);
  };

  const handleReservationSuccess = (reservationData) => {

    // Show success message
    alert(language === "ar" ?
      `تم حجز الوحدة رقم ${selectedParcelForReservation.parcel_number} بنجاح!` :
      `Unit ${selectedParcelForReservation.parcel_number} reserved successfully!`
    );

    // Close modal
    handleReservationClose();

    // Optionally, refresh the map data to show updated parcel status
    // You might want to emit an event or call a refresh function here
  };

  // Handle favorites
  const handleShowFavorites = () => {
    // Close any existing search results first
    setSearchResultsVisible(false);

    // Clear search results and set favorites mode
    setSearchResults(null);
    setCurrentSearchType('units');
    setIsSearchLoading(false);
    setIsFavoritesMode(true);
    // Clear single-view / project-card state so favorites starts clean
    setCustomSearchTitle(null);
    setHideUnitsTabInResults(false);
    setSearchResultsHistory([]);
    if (searchPanelRef.current && typeof searchPanelRef.current.setActiveTab === 'function') {
      searchPanelRef.current.setActiveTab('units');
    }

    // Show search results in favorites mode
    setSearchResultsVisible(true);
  };

  const handleCloseFavorites = () => {
    setFavoritesVisible(false);
  };

  const handleNavigateToUnit = (parcel) => {
    // This function will be called when user clicks on a favorite unit
    // We need to navigate to the unit on the map

    // Set the project if it's different from current
    let projectId = parcel.block?.project?.id || parcel.project?.id;
    
    if (projectId) {
      if (projectId !== selectedProject) {
        setSelectedProject(projectId);
      }
      setPendingUnitSelect(parcel);
    } else {
      // Use the parcel search function to navigate to the unit if no project info
      if (parcelSearchRef.current) {
        parcelSearchRef.current(parcel.id);
      }
    }
  };

  // Project search handlers
  const handleProjectSelect = (project) => {
    setSelectedProject(project.id);
  };

  const handleProjectSearch = (projectId) => {
    // Use the same function that OptimizedMap uses for project changes
    // This will trigger the same behavior as clicking on project marker
    setSelectedProject(projectId);

    // Also ensure we're at the right zoom level to show project details
    // The OptimizedMap component will handle the flyTo and panel display
  };

  const handleProjectFiltersChange = (filters) => {
    console.log('📥 App.js: handleProjectFiltersChange received:', filters);
    setProjectFilters(filters);
    console.log('✅ App.js: projectFilters state will be updated');
  };

  // Handle city zoom functionality
  const handleCityZoom = (cityData) => {
    if (mapRef.current && cityData.latitude && cityData.longitude) {
      try {
        // Zoom to the selected city with appropriate zoom level
        mapRef.current.flyTo({
          center: [cityData.longitude, cityData.latitude],
          zoom: 10, // City-level zoom
          duration: 2000, // 2 seconds animation
          essential: true // This animation is considered essential with respect to prefers-reduced-motion
        });
        
        console.log(`Zooming to city: ${cityData.name} at [${cityData.longitude}, ${cityData.latitude}]`);
      } catch (error) {
        console.error('Error zooming to city:', error);
      }
    } else {
      console.warn('Map reference not available or city coordinates missing');
    }
  };

  // Handle unified search results
  const handleShowSearchResults = (results, query = '', type = 'all') => {
    setSearchResults(results);
    searchPanelRef.current.loadMapBounds(results);

    setSearchResultsVisible(true);
    setIsSearchLoading(false);
    setCurrentSearchQuery(query);
    setCurrentSearchType(type);
    setIsFavoritesMode(false); // Reset favorites mode when showing search results
    setCustomSearchTitle(null); // Clear single-view title so back-to-results works
    setHideUnitsTabInResults(false);
    setSearchResultsHistory([]);
  };

  // Handle showing all projects/units for filtering
  const handleShowAllResults = async (type = 'all') => {
    setIsSearchLoading(true);
    setSearchResultsVisible(true);
    setCurrentSearchQuery('');
    setCurrentSearchType(type);
    setCustomSearchTitle(null);
    setHideUnitsTabInResults(false);
    setSearchResultsHistory([]);

    try {
      const response = await unifiedSearch('', type, 1, 10, {}, 'created_at', 'desc');
      if (response.data.success) {
        setSearchResults(response.data.data);
        //searchPanelRef loadMapBounds
        searchPanelRef.current.loadMapBounds(response.data.data);
      }
    } catch (error) {
      console.error('Error loading all results:', error);
    } finally {
      setIsSearchLoading(false);
    }
  };

  const handleStartSearch = () => {
    setIsSearchLoading(true);
    setSearchResultsVisible(true);
    setSearchResults(null);
  };

  // Handle pagination changes
  const handleSearchPageChange = async (tabType, page) => {
    if (!currentSearchQuery) return;

    setIsSearchLoading(true);

    try {
      const newPagination = {
        ...searchPagination,
        [tabType]: { ...searchPagination[tabType], page }
      };
      setSearchPagination(newPagination);

      const response = await unifiedSearch(
        currentSearchQuery,
        currentSearchType,
        page,
        searchPagination[tabType].perPage,
        searchFilters[tabType],
        searchSort[tabType].sortBy,
        searchSort[tabType].sortOrder
      );

      if (response.data.success) {
        setSearchResults(response.data.data);
                searchPanelRef.current.loadMapBounds(response.data.data);

      }
    } catch (error) {
      console.error('Error changing page:', error);
    } finally {
      setIsSearchLoading(false);
    }
  };

  const handleSearchItemsPerPageChange = async (tabType, perPage) => {
    if (!currentSearchQuery) return;

    setIsSearchLoading(true);

    try {
      const newPagination = {
        ...searchPagination,
        [tabType]: { page: 1, perPage }
      };
      setSearchPagination(newPagination);

      const response = await unifiedSearch(
        currentSearchQuery,
        currentSearchType,
        1,
        perPage,
        searchFilters[tabType],
        searchSort[tabType].sortBy,
        searchSort[tabType].sortOrder
      );

      if (response.data.success) {
        setSearchResults(response.data.data);
                searchPanelRef.current.loadMapBounds(response.data.data);

      }
    } catch (error) {
      console.error('Error changing items per page:', error);
    } finally {
      setIsSearchLoading(false);
    }
  };

  // Handle filters change
  const handleSearchFiltersChange = async (tabType, filters) => {

    setIsSearchLoading(true);

    // Ensure search results are visible when applying filters
    if (!searchResultsVisible) {
      setSearchResultsVisible(true);
    }

    try {
      const newFilters = {
        ...searchFilters,
        [tabType]: filters
      };
      setSearchFilters(newFilters);

      // Reset to first page when filters change
      const newPagination = {
        ...searchPagination,
        [tabType]: { ...searchPagination[tabType], page: 1 }
      };
      setSearchPagination(newPagination);

      // Use current search query or empty string
      const searchQuery = currentSearchQuery || '';
      const searchType = currentSearchType || 'all';

      //   query: searchQuery,
      //   type: searchType,
      //   page: 1,
      //   perPage: searchPagination[tabType].perPage,
      //   filters,
      //   sortBy: searchSort[tabType]?.sortBy || 'created_at',
      //   sortOrder: searchSort[tabType]?.sortOrder || 'desc'
      // });

      const response = await unifiedSearch(
        searchQuery,
        searchType,
        1,
        searchPagination[tabType].perPage,
        filters,
        searchSort[tabType]?.sortBy || 'created_at',
        searchSort[tabType]?.sortOrder || 'desc'
      );


      if (response.data.success) {
        setSearchResults(response.data.data);
                searchPanelRef.current.loadMapBounds(response.data.data);

      }
    } catch (error) {
      console.error('Error changing filters:', error);
    } finally {
      setIsSearchLoading(false);
    }
  };

  // Handle sort change
  const handleSearchSortChange = async (tabType, sortBy, sortOrder) => {

    setIsSearchLoading(true);

    // Ensure search results are visible when applying sort
    if (!searchResultsVisible) {
      setSearchResultsVisible(true);
    }

    try {
      const newSort = {
        ...searchSort,
        [tabType]: { sortBy, sortOrder }
      };
      setSearchSort(newSort);

      // Use current search query or empty string
      const searchQuery = currentSearchQuery || '';
      const searchType = currentSearchType || 'all';

      //   query: searchQuery,
      //   type: searchType,
      //   page: searchPagination[tabType].page,
      //   perPage: searchPagination[tabType].perPage,
      //   filters: searchFilters[tabType] || {},
      //   sortBy,
      //   sortOrder
      // });

      const response = await unifiedSearch(
        searchQuery,
        searchType,
        searchPagination[tabType].page,
        searchPagination[tabType].perPage,
        searchFilters[tabType] || {},
        sortBy,
        sortOrder
      );


      if (response.data.success) {
        setSearchResults(response.data.data);
                searchPanelRef.current.loadMapBounds(response.data.data);

      }
    } catch (error) {
      console.error('Error changing sort:', error);
    } finally {
      setIsSearchLoading(false);
    }
  };

  const handleCloseSearchResults = () => {
    // setSearchResultsVisible(false);
    // setSearchResults(null);
    setIsSearchLoading(false);
  };
  const handleCloseSearchResultsButton = () => {
  // console.log("handleCloseSearchResultsButton");
  // console.trace("Tracing the call stack");

    setSearchResultsVisible(false);
    setSearchResults(null);
    setIsSearchLoading(false);
    setIsFavoritesMode(false); // Reset favorites mode when closing
    setSearchResultsHistory([]);
    setCustomSearchTitle(null);
    setHideUnitsTabInResults(false);
  };

  const handleSearchProjectSelect = (project) => {
    // Use the same behavior as clicking on project in map
    if (handleProjectSearch) {
      handleProjectSearch(project.id);
    }
    // Also call setSelectedProject for additional handling
    setSelectedProject(project.id);
    handleCloseSearchResults();
  };

  // Handle project selection from map marker click - show in unified SearchResults
  const handleProjectSelectMap = (projectData) => {
    const title = language === 'ar'
      ? (projectData.name_ar || projectData.name_en || projectData.name || '')
      : (projectData.name_en || projectData.name_ar || projectData.name || '');

    // Push current results to history if visible
    if (searchResultsVisible) {
      const alreadyShowingThisProject =
        searchResults?.projects?.length === 1 &&
        searchResults.projects[0].id === projectData.id &&
        !(searchResults?.units?.length === 1);
      if (alreadyShowingThisProject) {
        setHideUnitsTabInResults(true);
        if (searchPanelRef.current && typeof searchPanelRef.current.expand === 'function') {
          searchPanelRef.current.expand();
        }
        if (searchPanelRef.current && typeof searchPanelRef.current.setActiveTab === 'function') {
          searchPanelRef.current.setActiveTab('projects');
        }
        return;
      }
      const isSingleView = Boolean(customSearchTitle);
      if (!isSingleView) {
        setSearchResultsHistory(prev => [
          ...prev,
          {
            results: searchResults,
            query: currentSearchQuery,
            type: currentSearchType,
            title: customSearchTitle,
            isFavoritesMode: isFavoritesMode
          }
        ]);
      }
    } else {
      setSearchResultsHistory([]);
    }

    setSearchResultsVisible(true);

    // Build developer fallback
    let developer = projectData.developer || {};
    if (!developer.id && !developer.name) {
      const devName = projectData.developer_name_ar
        || projectData.developer_name_en
        || projectData.developer_name;
      if (devName) {
        developer = {
          id: projectData.developer_id || 'unknown',
          name: devName,
          logo_url: projectData.developer_logo_url,
          projects_count: projectData.developer_projects_count || 1,
          city: projectData.city || null,
        };
      }
    }

    const results = {
      units: projectData.units || [],
      projects: [projectData],
      developers: developer.id || developer.name ? [developer] : [],
      summary: {
        total_units: projectData.units?.length || 0,
        total_projects: 1,
        total_developers: developer.id || developer.name ? 1 : 0,
      },
      pagination: { total: 1, current_page: 1, per_page: 10 }
    };

    setIsSearchLoading(false);
    setSearchResults(results);
    setCustomSearchTitle(title);
    setCurrentSearchType('all');
    setIsFavoritesMode(false);
    setHideUnitsTabInResults(true);

    if (searchPanelRef.current) {
      if (typeof searchPanelRef.current.expand === 'function') {
        searchPanelRef.current.expand();
      }
      if (typeof searchPanelRef.current.setActiveTab === 'function') {
        searchPanelRef.current.setActiveTab('projects');
      }
    }
  };

  const handleSearchUnitSelect = (unit) => {
    // Determine the mapProjectData or project id if possible
    let projectId = null;
    let projData = null;
    
    if (unit.project) {
      projectId = unit.project.id;
      projData = unit.project;
    } else if (unit.block && unit.block.project) {
      projectId = unit.block.project.id;
      projData = unit.block.project;
    }
    
    // Zoom map if necessary and trigger pending check so map layers load
    if (projectId) {
      if (projectId !== selectedProject) {
        setSelectedProject(projectId);
      }
      // This tells OrganizedMap to load the layers and pan to the unit
      setPendingUnitSelect(unit);
    }
    
    // Manage history and custom titles correctly
    handleParcelSelectMap(unit, projData);
  };

  const handleSearchDeveloperSelect = (developer) => {
    // You can implement developer-specific navigation here
    // For now, we'll just close the search results
    handleCloseSearchResults();
  };

  // Resolve shared-unit deep links (?project=X&shared=true&unit_id=Y) by
  // fetching the unit and routing it through the same selection path used
  // when a user clicks a unit in the search results. This works for both
  // residential and non-residential projects because pendingUnitSelect
  // owns floor loading + map-layer readiness.
  useEffect(() => {
    if (
      !isSharedUnitView ||
      !sharedUnitId ||
      sharedUnitResolvedRef.current ||
      !projects ||
      projects.length === 0
    ) {
      return;
    }

    sharedUnitResolvedRef.current = true;
    getParcelWithProject(sharedUnitId)
      .then((response) => {
        const unit = response?.data?.data;
        if (!unit) {
          console.warn('[shared-unit] not found:', sharedUnitId);
          return;
        }
        // Normalize the unit so the downstream selection paths can resolve the
        // owning project regardless of which relationship the API returns:
        // searchParcel relies on unit.block.project_id when the parcel isn't
        // yet loaded on the map and needs a floor load.
        const projectFromUnit = unit.project || unit.block?.project;
        const projectId = projectFromUnit?.id;
        if (projectId && unit.block && !unit.block.project_id) {
          unit.block = { ...unit.block, project_id: projectId };
        } else if (projectId && !unit.block) {
          unit.block = { project_id: projectId };
        }
        handleSearchUnitSelect(unit);
      })
      .catch((err) => {
        console.error('[shared-unit] failed:', err);
        sharedUnitResolvedRef.current = false;
      });
  }, [isSharedUnitView, sharedUnitId, projects]);

  // Handle map bounds update from search results
  const handleMapBoundsUpdate = (bounds) => {
    if (mapRef.current && bounds) {
      try {
        const isMobile = window.innerWidth < 768;
        const panelOnRight = actualSidebarPosition === 'right';
        // Reserve space for the search/favorites panel on the side so pins
        // near the screen edge don't end up hidden behind it.
        const sidePanelPadding = 460;
        const padding = isMobile
          ? { top: 120, bottom: 320, left: 40, right: 40 }
          : {
              top: 140,
              bottom: 140,
              left: panelOnRight ? 120 : sidePanelPadding,
              right: panelOnRight ? sidePanelPadding : 120,
            };

        // Choose maxZoom based on how spread out the results are.
        // A flat maxZoom=9 leaves a single result (or two nearby projects)
        // sitting inside a city-wide viewport; scale it to the bounds span instead.
        const lngSpan = Math.abs(bounds.maxLng - bounds.minLng);
        const latSpan = Math.abs(bounds.maxLat - bounds.minLat);
        const maxSpan = Math.max(lngSpan, latSpan);

        let maxZoom;
        if (maxSpan < 0.005) {
          // Single unit / parcel — get close enough to see the parcel itself.
          maxZoom = 11;
        } else if (maxSpan < 0.05) {
          // Same neighborhood / a couple of adjacent projects.
          maxZoom = 11;
        } else if (maxSpan < 0.5) {
          // Same city, multiple projects scattered.
          maxZoom = 11;
        } else if (maxSpan < 2) {
          // Across a metro area.
          maxZoom = 11;
        } else {
          // City-to-city spread — keep the wide view.
          maxZoom = 9;
        }

        fitMapToBounds(mapRef, bounds, {
          padding,
          maxZoom,
          duration: 1000,
        });
      } catch (error) {
        console.error('Error updating map bounds:', error);
      }
    }
  };

  const languageValue = {
    language,
    toggleLanguage,
    isRTL: language === "ar",
    sidebarPosition,
    toggleSidebarPosition,
    actualSidebarPosition,
    isRTLLayout,
  };

  return (
        <LanguageContext.Provider value={languageValue}>
          <GoogleAuthHandler />

        <div className={`App ${isRTLLayout ? "rtl" : "ltr"}`}>
          {/* Show project search mode at low zoom levels */}
          {currentZoom < 12 && !headerTransitioning && !isSharedView && (
            <Header
              selectedProject={selectedProject}
              setSelectedProject={setSelectedProject}
              mode="project"
              onProjectSelect={handleProjectSelect}
              onProjectSearch={handleProjectSearch}
              onProjectFiltersChange={handleProjectFiltersChange}
              projectFilters={projectFilters}
              onShowSearchResults={handleShowSearchResults}
              onShowAllResults={handleShowAllResults}
              onStartSearch={handleStartSearch}
              onCityZoom={handleCityZoom}
              isSharedView={isSharedView}
              mapInteracted={mapInteracted}
            />
          )}

          {/* Show unit search mode at high zoom levels */}
          {currentZoom >= 14 && !headerTransitioning && (
            <Header
              selectedProject={selectedProject}
              setSelectedProject={setSelectedProject}
              mode="unit"
              onParcelSearch={handleParcelSearch}
              onToggleFilters={toggleFilters}
              onStatusFiltersChange={handleStatusFiltersChange}
              statusFilters={statusFilters}
              onPriceFilterChange={handlePriceFilterChange}
              priceFilter={priceFilter}
              priceStats={priceStats}
              onUsageFilterChange={handleUsageFiltersChange}
              usageFilters={usageFilters}
              floorFilters={[]}
              projectFloors={projectFloors}
              onCityZoom={handleCityZoom}
              totalUnitsCount={1829}
              isSharedView={isSharedView}
              mapInteracted={mapInteracted}
            />
          )}

          {/* Loading indicator during header transition */}
          {headerTransitioning && (
            <div className="header-transition-loading">
              <div className="transition-spinner"></div>
            </div>
          )}
          <div className="main-content">
            <div className="map-wrapper">
              <ErrorBoundary>
                <OptimizedMap
                  selectedProject={selectedProject}
                  onProjectChange={setSelectedProject}
                  mapStyle={mapStyle}
                  onMapStyleChange={changeMapStyle}
                  filters={combineFilters()}
                  statusFilters={statusFilters}
                  priceFilter={priceFilter}
                  usageFilters={usageFilters}
                  floorFilters={[]}
                  projectFloors={projectFloors}
                  projectFilters={projectFilters}
                  onLanguageToggle={toggleLanguage}
                  onSidebarPositionToggle={toggleSidebarPosition}
                  language={language}
                  sidebarPosition={sidebarPosition}
                  actualSidebarPosition={actualSidebarPosition}
                  onParcelSearch={parcelSearchRef}
                  filtersVisible={filtersVisible}
                  onToggleFilters={toggleFilters}
                  isSharedView={isSharedView}
                  sharedProjectId={sharedProjectId}
                  isSharedUnitView={isSharedUnitView}
                  sharedUnitId={sharedUnitId}
                  onZoomChange={handleZoomChange}
                  onReserve={handleReservation}
                  onClearAllData={handleClearAllData}
                  onShowFavorites={handleShowFavorites}
                  onPriceStatsUpdate={handlePriceStatsUpdate}
                  onMapInteraction={handleMapInteraction}
                  onParcelSelect={handleParcelSelectMap}
                  onProjectSelect={handleProjectSelectMap}
                  pendingUnitSelect={pendingUnitSelect}
                  mapRef={mapRef}
                />
              </ErrorBoundary>
            </div>
          </div>

          {/* Reservation Modal */}
          <ReservationModal
            parcelData={selectedParcelForReservation}
            projectData={selectedProjectForReservation}
            language={language}
            isOpen={reservationModalOpen}
            onClose={handleReservationClose}
            onSuccess={handleReservationSuccess}
          />

          {/* Login Modal for Reservation */}
          <LoginModal
            isOpen={loginModalForReservationOpen}
            onClose={handleLoginModalCloseForReservation}
            onSuccess={handleLoginSuccessForReservation}
            language={language}
          />

          <FavoritesList
            isVisible={favoritesVisible}
            onClose={handleCloseFavorites}
            onNavigateToUnit={handleNavigateToUnit}
          />



          <SearchResults

          ref={searchPanelRef}
            searchResults={searchResults}
            isVisible={searchResultsVisible}
            isLoading={isSearchLoading}
            searchType={currentSearchType}
            hideUnitsTab={hideUnitsTabInResults}
            onClose={handleCloseSearchResultsButton}
            onProjectSelect={handleSearchProjectSelect}
            onUnitSelect={handleSearchUnitSelect}
            onDeveloperSelect={handleSearchDeveloperSelect}
            onToggleFavorite={async (parcelId) => {
              setFavoriteLoadingIds(prev => ({ ...prev, [parcelId]: true }));
              try {
                const response = await toggleFavorite(parcelId);
                if (response.data?.success) {
                  const isFavorited = response.data.is_favorited;
                  setSearchResults(current => {
                    if (!current?.units) return current;
                    return {
                      ...current,
                      units: current.units.map(u =>
                        u.id === parcelId
                          ? { ...u, is_favorite: isFavorited, is_favorited: isFavorited }
                          : u
                      ),
                    };
                  });
                }
              } catch (error) {
                console.error("Error toggling favorite:", error);
                if (error.response?.status === 401) {
                  alert(language === "ar" ? "يجب تسجيل الدخول أولاً" : "Please login first");
                }
              } finally {
                setFavoriteLoadingIds(prev => {
                  const next = { ...prev };
                  delete next[parcelId];
                  return next;
                });
              }
            }}
            favoriteLoadingIds={favoriteLoadingIds}
            onReservation={handleReservation}
            onPageChange={handleSearchPageChange}
            onItemsPerPageChange={handleSearchItemsPerPageChange}
            onFiltersChange={handleSearchFiltersChange}
            onSortChange={handleSearchSortChange}
            filters={searchFilters}
            searchSort={searchSort}
            isFavoritesMode={isFavoritesMode}
            onMapBoundsUpdate={handleMapBoundsUpdate}
            customTitle={customSearchTitle}
            hasHistory={searchResultsHistory.length > 0}
            onBackToHistory={handleBackToPreviousResults}
            isUnitBackgroundLoading={isUnitBackgroundLoading}
          />
        </div>
        </LanguageContext.Provider>
  );
}

// Main App component with Providers
function App() {
  return (
    <ErrorBoundary>
      <PreviewAccessGate>
        <ProjectsProvider>
          <GlobalModalsProvider>
            <AppContent />
          </GlobalModalsProvider>
        </ProjectsProvider>
      </PreviewAccessGate>
    </ErrorBoundary>
  );
}

export default App;
